import React, { useEffect, useState } from 'react';
import { Button, Tag, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../store/LanguageContext';

const { Title, Paragraph, Text } = Typography;

interface NewsArticleDetail {
  slug: string;
  title: string;
  summary: string;
  category?: string;
  date?: string;
  cover?: string;
  video?: string;
  videoPoster?: string;
  sourceName?: string;
  sourceUrl?: string;
  points?: string[];
  body?: string;
  disclaimer?: string;
  canonical?: string;
}

const NewsArticlePage: React.FC = () => {
  const { slug = '' } = useParams();
  const { language } = useLanguage();
  const zh = language === 'zh-CN';
  const [article, setArticle] = useState<NewsArticleDetail | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) {
        setMissing(true);
        return;
      }
      try {
        const res = await fetch(`/content/articles/${encodeURIComponent(slug)}.json?t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) {
          if (!cancelled) setMissing(true);
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setArticle(data);
          if (data.title) document.title = data.title;
          const desc = data.summary || '';
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', 'description');
            document.head.appendChild(meta);
          }
          meta.setAttribute('content', desc);
          let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
          if (!link) {
            link = document.createElement('link');
            link.rel = 'canonical';
            document.head.appendChild(link);
          }
          link.href = data.canonical || `${window.location.origin}/news/${slug}`;
        }
      } catch {
        if (!cancelled) setMissing(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (missing) {
    return (
      <div className="insights-container" style={{ padding: '48px 16px' }}>
        <Title level={3}>{zh ? '文章不存在' : 'Article not found'}</Title>
        <Button type="link">
          <Link to="/news">{zh ? '返回资讯' : 'Back to news'}</Link>
        </Button>
      </div>
    );
  }

  if (!article) {
    return <div className="insights-container" style={{ padding: 48 }}>{zh ? '加载中…' : 'Loading…'}</div>;
  }

  return (
    <div className="insights-page">
      <main className="insights-container" style={{ maxWidth: 820, paddingTop: 28 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} style={{ marginBottom: 12 }}>
          <Link to="/news">{zh ? '资讯列表' : 'All news'}</Link>
        </Button>
        {article.video ? (
          <img
            src={article.video}
            alt=""
            style={{ width: '100%', borderRadius: 12, marginBottom: 20, maxHeight: 420, objectFit: 'cover' }}
          />
        ) : article.cover ? (
          <img
            src={article.cover}
            alt=""
            style={{ width: '100%', borderRadius: 12, marginBottom: 20, maxHeight: 320, objectFit: 'cover' }}
          />
        ) : null}
        <div className="insight-card-meta" style={{ marginBottom: 12 }}>
          <Tag>{article.category || 'News'}</Tag>
          <span>{article.date}</span>
        </div>
        <Title level={1} style={{ marginTop: 0 }}>{article.title}</Title>
        <Paragraph type="secondary">{article.summary}</Paragraph>
        {article.points?.length ? (
          <ul style={{ lineHeight: 1.8, paddingLeft: 20 }}>
            {article.points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        {article.body ? (
          <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 16 }}>{article.body}</Paragraph>
        ) : null}
        <Paragraph type="secondary" style={{ marginTop: 24 }}>
          {article.disclaimer ||
            (zh
              ? '本站原创综述，非原文转载。请以官方来源为准。'
              : 'Original editorial summary. Not a wholesale reprint. Verify with the source.')}
        </Paragraph>
        {article.sourceUrl ? (
          <Text>
            {zh ? '官网：' : 'Official site: '}
            <a href={article.sourceUrl} target="_blank" rel="noopener noreferrer">
              {article.sourceName || article.sourceUrl}
            </a>
          </Text>
        ) : null}
      </main>
    </div>
  );
};

export default NewsArticlePage;
